/**
 * La foto del comprador, antes de subirla (BUS-03, BUS-04).
 *
 * Todo pasa en el navegador y en memoria. La foto no se guarda ni aca ni en el
 * servidor: se lee, se recorta, se sube, se compara y se descarta.
 *
 * Dos pasos, porque entre medio esta el recorte (rediseño del 14-09-2026, con
 * el buscador por foto de lowes.com como referencia):
 *
 * 1. `leerFoto` decodifica el archivo y lo deja en un lienzo de trabajo, ya
 *    derecho y reducido. De ahi sale la vista previa que se recorta.
 * 2. `recortarFoto` toma el pedazo elegido de ese lienzo y lo deja listo para
 *    subir: reducido otra vez y en JPEG.
 *
 * Reducir no es una optimizacion opcional. Una foto de celular son entre 3 y
 * 8 MB, y el servidor la va a llevar a 224 pixeles igual para compararla: subir
 * los 8 MB es hacer esperar treinta segundos en 4G para tirar el 98% de los
 * bytes. Reducida queda en unos 150 KB.
 *
 * De paso resuelve el HEIC del iPhone, que el servidor no sabe leer y que un
 * `<img>` tampoco muestra fuera de Safari: el navegador si lo decodifica con
 * `createImageBitmap`, y lo que sale del lienzo ya es JPEG.
 */

/**
 * Lado mayor del lienzo de trabajo. Mas grande que lo que se sube, para que un
 * recorte chico —un cuarto de la foto— todavia tenga pixeles de sobra.
 */
const LADO_TRABAJO = 1600;

/** Lado mayor de lo que se sube. Con 224 le basta al modelo; el margen es para el recorte. */
const LADO_MAXIMO = 1024;

/** Suficiente para un objeto sobre una mesa; mas calidad no cambia el resultado. */
const CALIDAD = 0.85;

/** Tope duro, el mismo que aplica el servidor. */
const MAXIMO_BYTES = 8 * 1024 * 1024;

export class ErrorDeFoto extends Error {
  constructor(mensaje: string) {
    super(mensaje);
    this.name = 'ErrorDeFoto';
  }
}

/** Que parte de la foto se sube, en fracciones de 0 a 1 del ancho y del alto. */
export interface Recorte {
  x: number;
  y: number;
  ancho: number;
  alto: number;
}

/** La foto entera: es con lo que arranca el recortador. */
export const RECORTE_COMPLETO: Recorte = { x: 0, y: 0, ancho: 1, alto: 1 };

/** La foto decodificada, derecha y reducida, lista para recortar. */
export interface FotoLeida {
  lienzo: HTMLCanvasElement;
  /** URL `blob:` para mostrarla en un `<img>`. Se libera con `liberarFoto`. */
  url: string;
  ancho: number;
  alto: number;
}

/** Medidas de destino conservando la proporcion. */
function medidasReducidas(
  ancho: number,
  alto: number,
  ladoMaximo: number,
): { ancho: number; alto: number } {
  const mayor = Math.max(ancho, alto);
  if (mayor <= ladoMaximo) return { ancho, alto };

  const factor = ladoMaximo / mayor;
  return {
    ancho: Math.max(1, Math.round(ancho * factor)),
    alto: Math.max(1, Math.round(alto * factor)),
  };
}

/**
 * Decodifica el archivo.
 *
 * `createImageBitmap` respeta la orientacion EXIF, que es la diferencia entre
 * mandar la foto derecha o de costado: casi todos los celulares guardan la foto
 * en horizontal con una etiqueta que dice como girarla.
 */
async function decodificar(archivo: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(archivo, { imageOrientation: 'from-image' });
  } catch {
    throw new ErrorDeFoto('No pudimos leer esa foto. Prueba con otra.');
  }
}

function lienzoEnBlanco(ancho: number, alto: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const lienzo = document.createElement('canvas');
  lienzo.width = ancho;
  lienzo.height = alto;

  const pincel = lienzo.getContext('2d');
  if (pincel === null) throw new ErrorDeFoto('No pudimos procesar esa foto.');

  // Fondo blanco: un PNG con transparencia sobre JPEG saldria con el fondo
  // negro y el modelo compararia contra una silueta oscura.
  pincel.fillStyle = '#ffffff';
  pincel.fillRect(0, 0, ancho, alto);
  return [lienzo, pincel];
}

async function aJpeg(lienzo: HTMLCanvasElement): Promise<Blob> {
  const blob = await new Promise<Blob | null>((resolver) => {
    lienzo.toBlob(resolver, 'image/jpeg', CALIDAD);
  });
  if (blob === null) throw new ErrorDeFoto('No pudimos procesar esa foto.');
  return blob;
}

/** Lee el archivo y lo deja derecho y reducido en un lienzo, con su vista previa. */
export async function leerFoto(archivo: File): Promise<FotoLeida> {
  if (!archivo.type.startsWith('image/')) {
    throw new ErrorDeFoto('Ese archivo no es una foto.');
  }
  if (archivo.size > MAXIMO_BYTES) {
    throw new ErrorDeFoto('Esa foto es demasiado pesada. Prueba con otra.');
  }

  const bitmap = await decodificar(archivo);

  try {
    const medidas = medidasReducidas(bitmap.width, bitmap.height, LADO_TRABAJO);
    const [lienzo, pincel] = lienzoEnBlanco(medidas.ancho, medidas.alto);
    pincel.drawImage(bitmap, 0, 0, medidas.ancho, medidas.alto);

    const url = URL.createObjectURL(await aJpeg(lienzo));
    return { lienzo, url, ancho: medidas.ancho, alto: medidas.alto };
  } finally {
    // Libera la memoria de la imagen decodificada sin esperar al recolector.
    bitmap.close();
  }
}

/** Sin esto cada foto elegida deja su blob retenido en memoria hasta cerrar la pestaña. */
export function liberarFoto(foto: FotoLeida): void {
  URL.revokeObjectURL(foto.url);
}

/**
 * Deja el pedazo elegido listo para subir: reducido y en JPEG.
 *
 * Devuelve un `Blob` y no un `File` porque no hay archivo: lo que viaja son los
 * bytes, sin nombre y sin nada que lo identifique.
 */
export async function recortarFoto(foto: FotoLeida, recorte: Recorte): Promise<Blob> {
  const origenX = Math.round(recorte.x * foto.ancho);
  const origenY = Math.round(recorte.y * foto.alto);
  const origenAncho = Math.max(1, Math.round(recorte.ancho * foto.ancho));
  const origenAlto = Math.max(1, Math.round(recorte.alto * foto.alto));

  const medidas = medidasReducidas(origenAncho, origenAlto, LADO_MAXIMO);
  const [lienzo, pincel] = lienzoEnBlanco(medidas.ancho, medidas.alto);
  pincel.drawImage(
    foto.lienzo,
    origenX,
    origenY,
    origenAncho,
    origenAlto,
    0,
    0,
    medidas.ancho,
    medidas.alto,
  );

  return aJpeg(lienzo);
}
